import { defineStore, acceptHMRUpdate } from "pinia";
import { ref } from "vue";
import { PaginatedData, PaginationArgs } from "@/types/api/common";
import client from "@/api/openapi-ts";
import type { components } from "@/api/schema.d.ts";

type schemas = components["schemas"];

type Loading = {
  workbooks: boolean;
  dashboardTypes: boolean;
  hosts: boolean;
  sites: boolean;
  siteUsers: boolean;
  siteWorkbooks: boolean;
  siteWorkbookViews: boolean;
  trustedUrl: boolean;
};

export const useTableauStore = defineStore("tableau", () => {
  const loading = ref<Loading>({
    workbooks: false,
    dashboardTypes: false,
    hosts: false,
    sites: false,
    siteUsers: false,
    siteWorkbooks: false,
    siteWorkbookViews: false,
    trustedUrl: false,
  });
  const saving = ref(false);
  const workbooks = ref<PaginatedData<schemas["TableauWorkbook"]>>({});
  const unpaginatedWorkbooks = ref<schemas["TableauWorkbook"][]>([]);
  const dashboardTypes = ref<schemas["TableauListDashboardTypes"]>({
    booking_optimization: 0,
    earned_media: 0,
    linear_optimization: 0,
    paid_media: 0,
  });
  const hosts = ref<schemas["TableauHostResponse"][]>([]);
  const sites = ref<schemas["TableauSiteResponse"][]>([]);
  const siteUsers = ref<schemas["TableauSiteUserResponse"][]>([]);
  const siteWorkbooks = ref<schemas["TableauSiteWorkbookResponse"][]>([]);
  const siteWorkbookViews = ref<schemas["TableauSiteWorkbookViewResponse"][]>(
    [],
  );

  return {
    loading,
    saving,
    workbooks,
    unpaginatedWorkbooks,
    dashboardTypes,
    hosts,
    sites,
    siteUsers,
    siteWorkbooks,
    siteWorkbookViews,

    async listWorkbooks(payload?: PaginationArgs) {
      loading.value.workbooks = true;
      const { data, error, response } = await client.GET(
        "/api/v1/tableau/workbooks",
        {
          params: {
            query: payload ?? {},
          },
        },
      );
      if (error) {
        loading.value.workbooks = false;
        return;
      }
      if (payload?.per_page) {
        workbooks.value = {
          pagination: {
            page: Number(response.headers.get("current-page")),
            page_items: Number(response.headers.get("page-items")),
            total_pages: Number(response.headers.get("total-pages")),
            total_items: Number(response.headers.get("total-count")),
          },
          data: data,
        };
      } else {
        unpaginatedWorkbooks.value = data as schemas["TableauWorkbook"][];
      }
      loading.value.workbooks = false;
    },

    async createWorkbook(
      workbook: schemas["TableauCreateWorkbookBodyRequest"],
    ) {
      saving.value = true;
      await client.POST("/api/v1/tableau/workbooks", {
        body: workbook,
      });
      saving.value = false;
    },

    async updateWorkbook(
      workbookId: number,
      workbook: schemas["TableauUpdateWorkbookBodyRequest"],
    ) {
      saving.value = true;

      ["id", "url", "username", "created_at", "updated_at"].forEach((key) => {
        delete workbook[key as keyof typeof workbook];
      });

      await client.PUT("/api/v1/tableau/workbooks/{tableau_workbook_id}", {
        params: { path: { tableau_workbook_id: workbookId } },
        body: workbook,
      });
      saving.value = false;
    },

    async deleteWorkbook(workbookId: number) {
      saving.value = true;
      await client.DELETE("/api/v1/tableau/workbooks/{tableau_workbook_id}", {
        params: { path: { tableau_workbook_id: workbookId } },
      });
      saving.value = false;
    },

    async listDashboardTypes() {
      loading.value.dashboardTypes = true;
      const { data } = await client.GET("/api/v1/tableau/workbooks/types");
      dashboardTypes.value = (data ??
        {}) as schemas["TableauListDashboardTypes"];
      loading.value.dashboardTypes = false;
    },

    async listHosts() {
      loading.value.hosts = true;
      const { data } = await client.GET("/api/v1/tableau/hosts");
      hosts.value = data as schemas["TableauHostResponse"][];
      loading.value.hosts = false;
    },

    async listSites(hostId: number) {
      loading.value.sites = true;
      const { data } = await client.GET(
        "/api/v1/tableau/{tableau_host_id}/sites",
        {
          params: {
            path: {
              tableau_host_id: hostId,
            },
          },
        },
      );
      sites.value = (data ?? []) as schemas["TableauSiteResponse"][];
      loading.value.sites = false;
    },

    async listSiteUsers(hostId: number, siteId: string) {
      loading.value.siteUsers = true;
      const { data } = await client.GET(
        "/api/v1/tableau/{tableau_host_id}/sites/{site_id}/users",
        {
          params: {
            path: {
              tableau_host_id: hostId,
              site_id: siteId,
            },
          },
        },
      );
      siteUsers.value = (data ?? []) as schemas["TableauSiteUserResponse"][];
      loading.value.siteUsers = false;
    },

    async listSiteWorkbooks(hostId: number, siteId: string) {
      loading.value.siteWorkbooks = true;
      const { data } = await client.GET(
        "/api/v1/tableau/{tableau_host_id}/sites/{site_id}/workbooks",
        {
          params: {
            path: {
              tableau_host_id: hostId,
              site_id: siteId,
            },
          },
        },
      );
      siteWorkbooks.value = (data ??
        []) as schemas["TableauSiteWorkbookResponse"][];
      loading.value.siteWorkbooks = false;
    },

    async listSiteWorkbookViews(
      hostId: number,
      siteId: string,
      workbookId: string,
    ) {
      loading.value.siteWorkbookViews = true;
      const { data } = await client.GET(
        "/api/v1/tableau/{tableau_host_id}/sites/{site_id}/workbooks/{tableau_workbook_id}/views",
        {
          params: {
            path: {
              tableau_host_id: hostId,
              site_id: siteId,
              tableau_workbook_id: workbookId,
            },
          },
        },
      );
      siteWorkbookViews.value = (data ??
        []) as schemas["TableauSiteWorkbookViewResponse"][];
      loading.value.siteWorkbookViews = false;
    },

    async getTrustedUrl(workbookId: number) {
      loading.value.trustedUrl = true;
      const { data, error } = await client.GET(
        "/api/v1/tableau/workbooks/{tableau_workbook_id}/trusted_url",
        {
          params: {
            path: {
              tableau_workbook_id: workbookId,
            },
          },
        },
      );
      loading.value.trustedUrl = false;
      return { data, error };
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTableauStore, import.meta.hot));
}
